<!--    Modal for creating and editing Devices  -->
<!--Copy right - © 2024 tecera gmbh-->

<script>
    import { fly, fade } from "svelte/transition";
    import {
        all_hymos,
        free_hymos,
        modal_mode,
        selected_device,
        toast_message,
    } from "../../stores.js";
    import { BASE_API_URL } from "../../config.js";
    import ModalInputField from "./form_parts/ModalInputField.svelte";
    import KoordinatenSetzen from "./form_parts/KoordinatenSetzen.svelte";
    import ToggleButton from "../buttons/ToggleButton.svelte";
    import { get_free_hymos, handle_bad_response } from "../../scripts/data.js";
    import ConfirmModal from "./ConfirmModal.svelte";

    export let is_open;
    export let device_type_name = "Gerät";
    export let hymo_group = "hymo";
    let is_button_disabled = false;
    
    // data for fields
    let title;
    let bezeichnung = null;
    let einbauort = null;
    let ortsteil = null;
    let strasse = null;
    let hausnummer = null;
    let longitude = null;
    let latitude = null;
    let ergaenzung = null;
    let bemerkung_lage = null;
    let nennweite = null;

    function clear_inputs() {
        bezeichnung = null;
        einbauort = null;
        ortsteil = null;
        strasse = null;
        hausnummer = null;
        longitude = null;
        latitude = null;
        ergaenzung = null;
        bemerkung_lage = null;
        nennweite = null;
    }

    // modal input fields
    let bezeichnung_field = {
        id: "bezeichnung",
        list_id: "alle_bezeichnungen",
        placeholder: "Bezeichnung",
    };
    
    let einbauort_field = {
        id: "einbauort",
        list_id: "alle_einbauorte",
        placeholder: "Einbauort",
    };
    
    let ortsteil_field = {
        id: "ortsteil",
        list_id: "alle_ortsteile",
        placeholder: "Ortsteil",
    };

    let strasse_field = {
        id: "strasse",
        list_id: "alle_strassen",
        placeholder: "Straße",
    };

    let hausnummer_field = {
        id: "hausnummer",
        list_id: "alle_hausnummern",
        placeholder: "Hausnummer",
    };

    let nennweite_field = {
        id: "nennweite",
        list_id: "alle_nennweiten",
        placeholder: "Nennweite [mm]",
    };
    
    let ergaenzung_field = {
        id: "ergaenzung",
        list_id: "alle_ergaenzungen",
        placeholder: "Ergänzung",
    };

    let bemerkung_lage_field = {
        id: "bemerkung_zur_lage",
        list_id: "alle_bemerkungen_zur_lage",
        placeholder: "Bemerkung zur Lage",
    };

    let selected_hymo_id; // the id of the available hymo without a device_config
    let device_active = true; // deviceconfig active status. true by default, but changed to the value of the actual config object
    let confirm_open = false; // whether the confirm modal is open
    let confirm_title =
        "Sind Sie sicher, dass Sie dieses Gerät löschen möchten?"; // title for the confirm modal
        let setManCoords = false; // if false the coordinates are created automiatcally
    let is_dropdown_open = false; // determines whether the dropdown is open or not
    let can_close = false; // determines whether the modal can be closed

    // reactivity to open and close the modal with delay
    $: {
        if (is_open) {
            document.body.style.overflow = "hidden";
            document.body.classList.add("modal-open");
            setTimeout(() => {
                can_close = true; // 250ms delay to prevent closing the modal immediately
            }, 250);
        } else {
            setTimeout(() => {
                document.body.style.overflow = "auto";
                document.body.classList.remove("modal-open");
            }, 350); // enable scrolling after the modal is closed
        }
    }
    
    function close_modal() {
        if (can_close) {
            is_open = false;
            $modal_mode = "closed";
            can_close = false;
        }
    }

    function timeout_button(){
        is_button_disabled = true;
        setTimeout(() => {
            is_button_disabled = false;
        }, 500);
    }

    // get pre-filled data for the form
    function get_form_data() {
        if ($modal_mode === "create") {
            title =  device_type_name + " anlegen";
            clear_inputs();
            selected_hymo_id = null;
            longitude = null;
            latitude = null;
            device_active = true;
        } else if ($modal_mode === "edit" && $selected_device) {
            title = device_type_name + " editieren";
            bezeichnung = $selected_device.name;
            einbauort = $selected_device.location;
            ortsteil = $selected_device.district;
            strasse = $selected_device.street;
            hausnummer = $selected_device.house_number;
            longitude = $selected_device.longitude;
            latitude = $selected_device.latitude;
            ergaenzung = $selected_device.comment;
            bemerkung_lage = $selected_device.comment_location;
            nennweite = $selected_device.nominal_diameter;
            device_active = $selected_device.active === 1 ? true : false;
        }
    }

    // reactivity to fetch correct data or clear inputs
    $: if (is_open) {
        get_form_data();
    }

    /**
     *
     * Funktion ermittelt die Koordinaten der genannten Addresse mit Hilfe von Nominatim von OSM
     * Falls Koordinaten nicht ermittelt werden können, werden null Werte zurückgegeben
     *
     */
    async function get_coords(address) {
        let url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${address}`;
        try {
            let response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error fetching coordinates`);
            }
            let data = await response.json();
            latitude = data[0].lat;
            longitude = data[0].lon;
            return [latitude, longitude];
        } catch (error) {
            latitude = null;
            longitude = null;
            return [latitude, longitude];
        }
    }

    // calling the API and saving the device after making some checks
    async function save_device() {
        timeout_button();
        // name is required
        if (!bezeichnung) {
            alert("Bitte Bezeichnung eingeben.");
            return;
        }

        // convert boolean to int to save it
        let device_active_int = device_active ? 1 : 0;
        if (!setManCoords) {
            await set_coords();
        }
        let device_data = {
            device: selected_hymo_id.id,
            name: bezeichnung,
            location: einbauort,
            district: ortsteil,
            street: strasse,
            house_number: hausnummer,
            longitude: parseFloat(longitude),
            latitude: parseFloat(latitude),
            comment: ergaenzung,
            comment_location: bemerkung_lage,
            nominal_diameter: parseInt(nennweite),
            active: device_active_int,
        };
        let url_extension = `/hymo_configs/`;
        let request_url = BASE_API_URL + url_extension;
        try {
            const response = await fetch(request_url, {
                method: "POST",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(device_data),
            });
            if (!response.ok) {
                handle_bad_response(response);
                return false;
            }
            let data = await response.json();
            all_hymos.update((hymos) => [...hymos, data.data]);
            $selected_device = data.data;
            close_modal();
            $toast_message = " Gerät erfolgreich angelegt";
            get_free_hymos(hymo_group);
            return true;
        } catch (error) {
            return false;
        }
    }

    // updating the device when editing
    async function update_device() {
        timeout_button();

        if (!bezeichnung) {
            alert("Bitte Bezeichnung eingeben.");
            return;
        }
        // if (!setManCoords) {
        //     await set_coords();
        // }
        let device_active_int = device_active ? 1 : 0;
        let device_config_id = $selected_device.id;
        let device_data = {
            name: bezeichnung,
            location: einbauort,
            district: ortsteil,
            street: strasse,
            house_number: hausnummer,
            longitude: parseFloat(longitude),
            latitude: parseFloat(latitude),
            comment: ergaenzung,
            comment_location: bemerkung_lage,
            nominal_diameter: parseInt(nennweite),
            active: device_active_int,
        };
        let url_extension = `/hymo_configs/${device_config_id}/`;
        let request_url = BASE_API_URL + url_extension;
        try {
            const response = await fetch(request_url, {
                method: "PUT",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(device_data),
            });

            if (!response.ok) {
                handle_bad_response(response);
                return false;
            }
            let data = await response.json();
            let idToFind = data.data.id;
            let index = $all_hymos.findIndex(
                (device_config) => device_config.id == idToFind,
            );
            $all_hymos[index] = data.data;
            $selected_device = data.data;
            close_modal();
            $toast_message = " Gerät erfolgreich editiert";
            return true;
        } catch (error) {
            return false;
        }
    }

    // delete request to the API
    async function delete_device() {
        timeout_button();

        let device_config_id = $selected_device.id;
        let url_extension = `/hymo_configs/${device_config_id}/`;
        let request_url = BASE_API_URL + url_extension;
        try {
            const response = await fetch(request_url, {
                method: "DELETE",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                handle_bad_response(response);
                return false;
            }
            let data = await response.json();

            close_modal();
            get_free_hymos(hymo_group);
            $toast_message = " Gerät erfolgreich gelöscht";

            let idToFind = device_config_id;
            let index = $all_hymos.findIndex(
                (device_config) => device_config.id == idToFind,
            );
            if (index !== -1) {
                $all_hymos.splice(index, 1);
            }
            $all_hymos = $all_hymos;
            $selected_device = $all_hymos[0];
            return true;
        } catch (error) {
            return false;
        }
    }

    async function set_coords() {
        // Zusammengesetzte Addresse aufbauen
        let ort = einbauort ? einbauort : "";
        let str = strasse ? strasse : "";
        let nmr = hausnummer ? hausnummer : "";

        var full_address = ort + " " + str + " " + nmr;

        // Koordinaten von Nominatim abfragen
        var coords = await get_coords(full_address);

        // Koordniaten in vorgesehene Felder schreiben, damit sie beim Speichern in die DB übernommen werden
        if (coords.length !== 0) {
            latitude = coords[0];
            longitude = coords[1];
        }
        // Sollten keine Koordinaten geliefert werden --> Alarm ausgeben
        else {
            alert("Addresse konnte nicht gefunden werden");
        }
    }

    function select_hymo(hymo) {
        selected_hymo_id = hymo;
        is_dropdown_open = false;
    }

    function handleMenuOpen() {
        if (is_dropdown_open) {
            is_dropdown_open = false;
            document.body.removeEventListener("click", handleMenuClose);
        } else {
            is_dropdown_open = true;
            document.body.addEventListener("click", handleMenuClose);
        }
    }
    function handleMenuClose() {
        is_dropdown_open = false;
        document.body.removeEventListener("click", handleMenuClose);
    }
</script>

{#if is_open}
    <div
        class="svelte-modal svelte-backdrop"
        tabindex="-1"
        role="dialog"
        on:mousedown|self={() => close_modal()}
        on:keydown={(e) => {
            if (e.key === "Escape") {
                close_modal();
            }
        }}
        transition:fade={{ duration: 150 }}
    >
        <div
            class="modal-dialog modal-dialog-centered"
            role="document"
            in:fly={{ y: -400, duration: 350, delay: 100 }}
            out:fly={{ y: -400, duration: 350 }}
        >
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="deleteTilte">
                        {title}
                    </h5>
                    <button
                        type="button"
                        class="close"
                        on:click={() => close_modal()}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <!-- Modal Body -->
                <div class="modal-body">
                    <div class="input-group mb-0 mt-2">
                        <div class="input-group mb-2 mt-2">
                            {#if $modal_mode == "create"}
                                {#if $free_hymos.length == 0}
                                    <button
                                        class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                        id="geraete_id"
                                        type="button"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                        disabled="true"
                                    >
                                        Keine freien Sensoren vorhanden
                                    </button>
                                {:else}
                                    <button
                                        class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                        id="geraete_id"
                                        type="button"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                        on:click|stopPropagation={handleMenuOpen}
                                    >
                                        {selected_hymo_id
                                            ? selected_hymo_id.dev_eui
                                            : "Sensor wählen"}
                                    </button>
                                {/if}
                            {:else}
                                <button
                                    class="btn btn-normal btn-transparentBlack btn-block dropdown-toggle"
                                    id="geraete_id"
                                    type="button"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    disabled="true"
                                >
                                    {#if $selected_device}
                                        {$selected_device.dev_eui}
                                    {/if}
                                </button>
                            {/if}

                            <div
                                class="dropdown-menu svelte-dropdown filter_dropdown mt-2 {is_dropdown_open
                                    ? 'show'
                                    : ''}"
                                aria-labelledby="geraete_id"
                            >
                                <div
                                    class="filter_dropdown_scrollable"
                                    id="alle_geraete_ids"
                                >
                                    {#each $free_hymos as hymo}
                                        <button
                                            class="btn btn-transparent btn-block dropdown__checkbox"
                                            value={hymo.dev_eui}
                                            on:click={() => select_hymo(hymo)}
                                            >{hymo.dev_eui}</button
                                        >
                                    {/each}
                                </div>
                            </div>
                        </div>
                        <ModalInputField
                            field_data={bezeichnung_field}
                            bind:input_value={bezeichnung}
                        />
                        <ModalInputField
                            field_data={einbauort_field}
                            bind:input_value={einbauort}
                        />
                        <ModalInputField
                            field_data={ortsteil_field}
                            bind:input_value={ortsteil}
                        />
                        <ModalInputField
                            field_data={strasse_field}
                            bind:input_value={strasse}
                        />
                        <ModalInputField
                            field_data={hausnummer_field}
                            bind:input_value={hausnummer}
                        />
                        <ModalInputField
                            field_data={nennweite_field}
                            bind:input_value={nennweite}
                        />
                        <ModalInputField
                            field_data={ergaenzung_field}
                            bind:input_value={ergaenzung}
                        />
                        <ModalInputField
                            field_data={bemerkung_lage_field}
                            bind:input_value={bemerkung_lage}
                        />

                        <ToggleButton
                            id="aktiv"
                            label="Aktiv"
                            bind:toggle={device_active}
                            classes={"mt-2"}
                        />
                        <KoordinatenSetzen
                            classes={"mt-2"}
                            bind:longitude
                            bind:latitude
                            bind:setManCoords
                            {set_coords}
                        />

                        <button
                            type="button"
                            class="btn btn-secondary mr-auto mt-2"
                            disabled>Excel import</button
                        >
                    </div>
                </div>
                <!-- Modal Footer -->
                <div class="modal-footer mt-2">
                    <!-- {#if $modal_mode == "edit"}
                        <button
                            type="button"
                            class="btn btn-danger mr-auto"
                            on:click={() => (confirm_open = true)}
                            >Löschen</button
                        >
                    {/if} -->
                    <button
                        type="button"
                        class="btn btn-secondary"
                        on:click={() => close_modal()}>Abbrechen</button
                    >
                    {#if $modal_mode == "create"}
                        <button
                            type="button"
                            class="btn btn-primary"
                            disabled={!selected_hymo_id || is_button_disabled}
                            on:click={() => save_device()}>Speichern</button
                        >
                    {:else}
                        <button
                            type="button"
                            class="btn btn-primary"
                            disabled={is_button_disabled}
                            on:click={() => update_device()}>Speichern</button
                        >
                    {/if}
                </div>
            </div>
        </div>
    </div>
    <ConfirmModal
        bind:show_modal={confirm_open}
        title={confirm_title}
        on:confirm={() => delete_device()}
    />
{/if}
